import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Card, brandFillClass } from "@/components/ui";
import { cn } from "@/lib/utils";
import { customerInitials } from "./customerInitials";

export interface BookingDetailHeaderMeta {
  icon: LucideIcon;
  label: string;
}

interface BookingDetailHeaderProps {
  title: string;
  /** Booking customer, shown as the avatar initials (the title may be the activity). */
  customerName: string;
  /** Status badge (or other chip) beside the title. */
  badge?: ReactNode;
  /** Key facts as chips under the title (activity, visit date, guests…). */
  meta?: BookingDetailHeaderMeta[];
  /** Right-aligned slot (amount, actions). */
  actions?: ReactNode;
  /** Overflow menu trigger: pinned top-right on mobile, after `actions` on desktop. */
  menu?: ReactNode;
  /** Rendered under a divider inside the hero (e.g. lifecycle timeline). */
  children?: ReactNode;
  className?: string;
}

/** Booking detail hero: customer avatar, name + status, fact chips, actions and an optional footer (timeline). */
export function BookingDetailHeader({
  title,
  customerName,
  badge,
  meta = [],
  actions,
  menu,
  children,
  className,
}: BookingDetailHeaderProps) {
  return (
    <Card className={cn("relative animate-slide-up overflow-hidden", className)}>
      <div
        className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full bg-primary-400/15 blur-3xl"
        aria-hidden
      />
      <div className="relative flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div className={cn("flex min-w-0 items-start gap-4", menu && "pr-12 sm:pr-0")}>
          <span
            className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-base font-semibold tracking-tight shadow-sm ring-1 ring-inset ring-white/15", brandFillClass("primary"))}
            aria-hidden
          >
            {customerInitials(customerName)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="min-w-0 text-balance text-xl font-semibold tracking-tight text-surface-900 sm:text-2xl">
                {title}
              </h1>
              {badge}
            </div>
            {meta.length > 0 ? (
              <ul className="mt-2.5 flex flex-wrap items-center gap-2">
                {meta.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-surface-200 bg-surface-50 px-2 py-1 text-xs font-medium text-surface-700 dark:bg-surface-100/50"
                  >
                    <Icon
                      className="h-3.5 w-3.5 shrink-0 text-surface-400"
                      aria-hidden
                    />
                    <span className="min-w-0 truncate tabular-nums">{label}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        {actions || menu ? (
          <div className="flex shrink-0 items-start gap-4 sm:gap-5 sm:pt-1">
            {actions}
            {menu ? (
              <div className="absolute right-4 top-4 sm:static">{menu}</div>
            ) : null}
          </div>
        ) : null}
      </div>
      {children ? (
        <div className="relative border-t border-surface-200/70 bg-surface-50/50 px-5 py-4 dark:bg-surface-100/20">
          {children}
        </div>
      ) : null}
    </Card>
  );
}
